// Composition root: the only place that imports from @pack-it-up/data and wires
// concrete implementations into the interfaces declared in @pack-it-up/core.
import type { AllRepositories, CopyPlan, TemplateSyncPlan } from "@pack-it-up/core";
import type { Clock, SecureStorage } from "@pack-it-up/viewmodel";
import { todayIn, type DomainError, type Result } from "@pack-it-up/shared";
import { initFirebase, makeFirebaseRepos, makeInMemoryRepos } from "@pack-it-up/data";

// Read Vite env without depending on vite/client types being present at typecheck.
const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env ?? {};

const stage = env.VITE_STAGE ?? "prod";
const isDev = env.VITE_IS_DEV === "true";

// Full prod (or beta) config sourced from VITE_FIREBASE_* env vars.
// Built by omitting absent keys so the object type satisfies exactOptionalPropertyTypes.
const envConfig: Parameters<typeof initFirebase>[0] = {
  ...(env.VITE_FIREBASE_API_KEY !== undefined ? { apiKey: env.VITE_FIREBASE_API_KEY } : {}),
  ...(env.VITE_FIREBASE_AUTH_DOMAIN !== undefined ? { authDomain: env.VITE_FIREBASE_AUTH_DOMAIN } : {}),
  ...(env.VITE_FIREBASE_PROJECT_ID !== undefined ? { projectId: env.VITE_FIREBASE_PROJECT_ID } : {}),
  ...(env.VITE_FIREBASE_STORAGE_BUCKET !== undefined ? { storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET } : {}),
  ...(env.VITE_FIREBASE_MESSAGING_SENDER_ID !== undefined ? { messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID } : {}),
  ...(env.VITE_FIREBASE_APP_ID !== undefined ? { appId: env.VITE_FIREBASE_APP_ID } : {}),
};

// `VITE_USE_FAKES=1` runs entirely on the in-memory backend (no network / no Firebase
// config needed) — for local UI work, the demo seed, and Playwright.
export const useFakes = env.VITE_USE_FAKES === "1";

// `VITE_FIREBASE_EMULATOR=1` runs the real Firebase SDK against the local emulators
// (real firestore.rules, no production data) — for debugging rules issues locally.
export const useEmulators = env.VITE_FIREBASE_EMULATOR === "1";

// The emulators accept any non-empty apiKey; the projectId must match the one the
// emulator suite was started with (see the `emulator` script in packages/data).
// The apiKey is deliberately NOT read from env: Firebase Auth persists sessions in
// IndexedDB keyed by apiKey, so reusing the prod key would restore a prod session
// against the emulator — its token has the wrong audience, and Firestore then treats
// every request as unauthenticated.
const emulatorConfig: Parameters<typeof initFirebase>[0] = {
  apiKey: "emulator-fake-api-key",
  projectId: env.VITE_FIREBASE_PROJECT_ID ?? "packitup-emulator-test",
};

const firebaseConfig = isDev ? emulatorConfig : envConfig;

console.log(`[pack-it-up] stage: ${stage}, emulator: ${useEmulators}`);

// Optional host:port overrides for the emulators — the browser can't read the
// *_EMULATOR_HOST process env vars, so setups on non-default ports (e2e) pass
// them as VITE_ vars instead.
const emulatorHosts: { firestore?: string; auth?: string; functions?: string } = {
  ...(env.VITE_EMULATOR_FIRESTORE_HOST !== undefined ? { firestore: env.VITE_EMULATOR_FIRESTORE_HOST } : {}),
  ...(env.VITE_EMULATOR_AUTH_HOST !== undefined ? { auth: env.VITE_EMULATOR_AUTH_HOST } : {}),
  ...(env.VITE_EMULATOR_FUNCTIONS_HOST !== undefined ? { functions: env.VITE_EMULATOR_FUNCTIONS_HOST } : {}),
};

// The repositories plus the plan-applier seams that are not part of `AllRepositories`
// (they apply a Domain plan as one batch rather than implementing an interface). Both
// backends expose them; naming them here is what lets the app use them without probing.
export interface AppRepositories extends AllRepositories {
  // Doc 13 §5 — awaits its chunked commits and reports, unlike the import applier.
  applyTemplateSyncPlan: (plan: TemplateSyncPlan) => Promise<Result<void, DomainError>>;
  // Issue #8 — duplicate a template / save a trip as a template / copy a trip.
  applyCopyPlan: (plan: CopyPlan) => Promise<Result<void, DomainError>>;
}

// Build the repository set for the app.
export const makeRepositories = (): AppRepositories => {
  if (useFakes) return makeInMemoryRepos();
  if (useEmulators) return makeFirebaseRepos(initFirebase(emulatorConfig, { emulators: true, emulatorHosts }));
  return makeFirebaseRepos(initFirebase(firebaseConfig, { autoDetectLongPolling: true }));
};

// Web implementation of the Clock capability (date-dependent ViewModel logic, e.g. the
// home trip grouping). `today` resolves the calendar date in the given IANA zone.
export const makeWebClock = (): Clock => ({
  now: () => new Date(),
  today: (timezone) => todayIn(timezone, new Date()),
});

// Web implementation of the SecureStorage capability (used for the persisted theme
// preference). localStorage is synchronous; the interface is async, so wrap it.
export const makeWebSecureStorage = (): SecureStorage => ({
  async getItem(key) {
    return localStorage.getItem(key);
  },
  async setItem(key, value) {
    localStorage.setItem(key, value);
  },
  async removeItem(key) {
    localStorage.removeItem(key);
  },
});
